import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, RefreshCw, Wifi, WifiOff } from 'lucide-react';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { es } from 'date-fns/locale/es';
import { SyncPill, useRetrySync } from '@/components/FlujoSyncBadge';
import { ProveedoresSyncPanel } from '@/components/ProveedoresSyncPanel';
import { StoreMappingPanel } from '@/components/StoreMappingPanel';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PARTE = { ADL: 'Con adelanto', GER: 'Pagado por Gerencia' };

const fmt = (d) => { try { return format(new Date(d), 'dd MMM yy HH:mm', { locale: es }); } catch { return '—'; } };

function UrlEditor({ current, onSaved }) {
  const [url, setUrl] = useState(current || '');
  const save = async () => {
    try {
      await axios.put(`${API}/flujo-sync/config`, { url });
      toast.success('Conexión guardada');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo guardar'); }
  };
  return (
    <div className="flex flex-col sm:flex-row gap-2 mt-3" data-testid="flujo-url-editor">
      <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://flujocaja.tiendassiempre.com"
        className="flex-1 h-9 rounded-md border border-slate-300 dark:border-slate-700 bg-transparent px-3 text-sm" data-testid="flujo-url-input" />
      <Button size="sm" onClick={save} className="bg-[#2c57c5] hover:bg-[#1e3a8a] text-white h-9" data-testid="flujo-url-save">Guardar dirección</Button>
    </div>
  );
}

function StatusPanel({ st, isAdmin, onSaved }) {
  if (!st) return null;
  const ok = st.configurado && st.conectado;
  return (
    <Card className="p-5 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800" data-testid="flujo-status-card">
      <div className="flex items-center gap-2 mb-3">
        {ok ? <Wifi className="h-4 w-4 text-emerald-600" /> : <WifiOff className="h-4 w-4 text-red-600" />}
        <span className="font-semibold text-slate-800 dark:text-slate-100" data-testid="flujo-connection-status">
          {!st.configurado ? 'Integración no configurada' : ok ? 'Conectado a Flujo de Caja' : 'Sin conexión con Flujo de Caja'}
        </span>
        <span className="ml-auto text-xs text-slate-500 dark:text-slate-400" data-testid="flujo-pending-count">{st.pendientes} pendientes</span>
      </div>
      <p className="text-xs text-slate-500 dark:text-slate-400 mb-2" data-testid="flujo-entorno">
        Entorno: <span className="font-semibold">{st.entorno === 'preview' ? 'Vista previa' : 'Producción'}</span> → {st.url}
      </p>
      {st.error && <p className="text-xs text-red-600 dark:text-red-400 mb-2">{st.error}</p>}
      {isAdmin && <UrlEditor key={st.url} current={st.url} onSaved={onSaved} />}
    </Card>
  );
}

function SyncRow({ r, onRetry, navigate }) {
  const link = r.origen === 'adelanto' ? `/adelantos/${r.origen_id}` : `/payments/${r.origen_id}`;
  return (
    <tr className="border-t border-slate-100 dark:border-slate-800 align-top" data-testid={`sync-row-${r.id}`}>
      <td className="px-3 py-2 whitespace-nowrap text-slate-600 dark:text-slate-400">{fmt(r.created_at)}</td>
      <td className="px-3 py-2">
        <button className="text-[#2c57c5] hover:underline font-medium" onClick={() => navigate(link)}>
          {r.tipo === 'ingreso' ? 'Ingreso · Adelanto' : `Gasto · ${PARTE[r.parte] || r.parte}`}
        </button>
        <p className="text-[11px] text-slate-500 dark:text-slate-400">{r.payload?.proveedor_nombre || r.payload?.numero_comprobante || ''}</p>
      </td>
      <td className="px-3 py-2 text-right tabular-nums font-semibold">Q{Number(r.monto).toFixed(2)}</td>
      <td className="px-3 py-2"><SyncPill status={r.status} />
        {r.error && r.status !== 'enviado' && <p className="text-[11px] text-red-600 dark:text-red-400 mt-1 max-w-xs break-words">{r.error}</p>}
      </td>
      <td className="px-3 py-2 text-right">
        {(r.status === 'pendiente' || r.status === 'anulacion_pendiente') && (
          <Button size="sm" variant="outline" onClick={() => onRetry(r.id)} data-testid={`retry-${r.id}`}>Reintentar</Button>
        )}
      </td>
    </tr>
  );
}

export default function FlujoSync() {
  const navigate = useNavigate();
  const [st, setSt] = useState(null);
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState('all');
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [desde, setDesde] = useState(new Date().toISOString().slice(0, 10));

  const load = useCallback(() => {
    axios.get(`${API}/flujo-sync/status`).then(r => setSt(r.data)).catch(() => {});
    axios.get(`${API}/flujo-sync`, { params: filter === 'all' ? {} : { status: filter } }).then(r => setRows(r.data || [])).catch(() => toast.error('Error al cargar'));
  }, [filter]);
  const retry = useRetrySync(load);
  useEffect(() => { load(); }, [load]);

  const retryAll = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/flujo-sync/retry-pending`);
      toast.success(`${r.data.enviados} de ${r.data.total} sincronizados`);
      load();
    } catch (e) { toast.error('No se pudo reintentar'); } finally { setBusy(false); }
  };

  const backfill = async () => {
    if (!window.confirm(`Se enviarán al Flujo los pagos aprobados y adelantos de tiendas desde ${desde} (sin duplicar). ¿Continuar?`)) return;
    setBusy(true);
    try {
      await axios.post(`${API}/flujo-sync/backfill`, { desde });
      toast.success('Enviando en segundo plano. La lista se actualizará en unos segundos.', { duration: 6000 });
      setTimeout(load, 8000);
      setTimeout(load, 30000);
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo enviar'); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950" data-testid="flujo-sync-page">
      <div className="container mx-auto px-4 py-4 space-y-5">
        <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="-ml-2" data-testid="back-to-dashboard-btn">
          <ArrowLeft className="h-4 w-4 mr-2" /> Volver al Dashboard
        </Button>
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-display font-bold text-slate-900 dark:text-slate-100">Sincronización Flujo de Caja</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">Adelantos (ingresos) y pagos aprobados (gastos) enviados a las tiendas.</p>
          </div>
          <div className="flex gap-2">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-44" data-testid="sync-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="pendiente,anulacion_pendiente">Pendientes</SelectItem>
                <SelectItem value="enviado">Sincronizados</SelectItem>
                <SelectItem value="anulado,cancelado">Anulados</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={retryAll} disabled={busy} className="bg-[#2c57c5] hover:bg-[#1e3a8a] text-white" data-testid="retry-all-btn">
              <RefreshCw className={`h-4 w-4 mr-2 ${busy ? 'animate-spin' : ''}`} /> Reintentar pendientes
            </Button>
          </div>
        </div>
        <StatusPanel st={st} isAdmin={isAdmin} onSaved={load} />
        {isAdmin && <Card className="p-4 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="backfill-card">
          <p className="text-sm text-slate-700 dark:text-slate-300 flex-1">Enviar al Flujo pagos aprobados y adelantos que no se enviaron, desde:</p>
          <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="h-9 rounded-md border border-slate-300 dark:border-slate-700 bg-transparent px-2 text-sm" data-testid="backfill-date" />
          <Button onClick={backfill} disabled={busy} variant="outline" data-testid="backfill-btn">Enviar pendientes</Button>
        </Card>}
        <StoreMappingPanel />
        <ProveedoresSyncPanel onSynced={load} />
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 overflow-x-auto">
          <table className="w-full text-sm" data-testid="sync-table">
            <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-600 dark:text-slate-300 text-[10px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2">Fecha</th><th className="text-left px-3 py-2">Movimiento</th>
                <th className="text-right px-3 py-2">Monto</th><th className="text-left px-3 py-2">Estado</th><th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map(r => <SyncRow key={r.id} r={r} onRetry={retry} navigate={navigate} />)}
              {rows.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-slate-500">Sin movimientos</td></tr>}
            </tbody>
          </table>
        </Card>
      </div>
    </div>
  );
}
